import { useState, useEffect, useCallback } from 'react';
import { Loader2, Plus, Trash2, AlertOctagon, AlertTriangle, Circle, Rocket } from 'lucide-react';
import { workshopApi } from '../../../services/api';
import {
  type BacklogItem,
  type BacklogStatus,
  type BacklogPriority,
  BACKLOG_STATUS_LABEL,
  BACKLOG_PRIORITY_LABEL,
} from '../../../types';

const PRIORITY_META: Record<BacklogPriority, { icon: typeof Circle; color: string }> = {
  critical: { icon: AlertOctagon, color: 'text-red-600 dark:text-red-400' },
  high:     { icon: AlertTriangle, color: 'text-amber-600 dark:text-amber-400' },
  medium:   { icon: Circle, color: 'text-blue-600 dark:text-blue-400' },
  low:      { icon: Circle, color: 'text-gray-400 dark:text-gray-500' },
};

const STATUS_BG: Record<BacklogStatus, string> = {
  new:         'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
  triaged:     'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300',
  in_progress: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
  in_uat:      'bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300',
  shipped:     'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
  wont_fix:    'bg-gray-100 dark:bg-surface-850 text-gray-500 dark:text-gray-400',
};

export default function BacklogView({ onOpenFeature: _onOpenFeature }: { onOpenFeature: (id: string) => void }) {
  void _onOpenFeature; // Reserved for future "open feature from item" affordance
  const [items, setItems] = useState<BacklogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<BacklogStatus | ''>('');
  const [priorityFilter, setPriorityFilter] = useState<BacklogPriority | ''>('');
  const [showNewForm, setShowNewForm] = useState(false);
  const [draftTitle, setDraftTitle] = useState('');
  const [draftPriority, setDraftPriority] = useState<BacklogPriority>('medium');

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await workshopApi.listItems({
        status: statusFilter || undefined,
        priority: priorityFilter || undefined,
      });
      setItems(list);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load backlog');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, priorityFilter]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleStatusChange = async (item: BacklogItem, status: BacklogStatus) => {
    const updated = await workshopApi.updateItem(item.id, { status });
    setItems((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
  };

  const handleDelete = async (item: BacklogItem) => {
    if (!confirm(`Delete "${item.title}"?`)) return;
    await workshopApi.deleteItem(item.id);
    setItems((prev) => prev.filter((i) => i.id !== item.id));
  };

  const handleCreate = async () => {
    if (!draftTitle.trim()) return;
    const created = await workshopApi.createItem({
      title: draftTitle.trim(),
      priority: draftPriority,
      source_type: 'manual',
    });
    setItems((prev) => [created, ...prev]);
    setDraftTitle('');
    setShowNewForm(false);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Filters + new */}
      <div className="flex items-end gap-3 mb-3 flex-wrap">
        <div>
          <label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">Status</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as BacklogStatus | '')}
            className="px-2 py-1 text-sm border border-gray-300 dark:border-surface-800 rounded bg-white dark:bg-surface-850 text-gray-900 dark:text-white"
          >
            <option value="">All</option>
            {Object.entries(BACKLOG_STATUS_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">Priority</label>
          <select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value as BacklogPriority | '')}
            className="px-2 py-1 text-sm border border-gray-300 dark:border-surface-800 rounded bg-white dark:bg-surface-850 text-gray-900 dark:text-white"
          >
            <option value="">All</option>
            {Object.entries(BACKLOG_PRIORITY_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <button
          onClick={() => setShowNewForm(true)}
          className="ml-auto inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-primary-600 hover:bg-primary-700 text-white rounded font-medium"
        >
          <Plus size={14} />
          New item
        </button>
      </div>

      {showNewForm && (
        <div className="mb-3 p-3 border border-gray-200 dark:border-surface-800 rounded bg-white dark:bg-surface-900">
          <input
            type="text"
            value={draftTitle}
            onChange={(e) => setDraftTitle(e.target.value)}
            placeholder="Title (e.g. 'Add contrast inference for non-contrast head CTs')"
            className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-surface-800 rounded bg-white dark:bg-surface-850 text-gray-900 dark:text-white mb-2"
          />
          <div className="flex items-center gap-2">
            <select
              value={draftPriority}
              onChange={(e) => setDraftPriority(e.target.value as BacklogPriority)}
              className="px-2 py-1 text-xs border border-gray-300 dark:border-surface-800 rounded bg-white dark:bg-surface-850 text-gray-900 dark:text-white"
            >
              {Object.entries(BACKLOG_PRIORITY_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <button
              onClick={handleCreate}
              disabled={!draftTitle.trim()}
              className="px-2 py-1 text-xs bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white rounded font-medium"
            >
              Create
            </button>
            <button
              onClick={() => { setShowNewForm(false); setDraftTitle(''); }}
              className="px-2 py-1 text-xs text-gray-500 hover:bg-gray-100 dark:hover:bg-surface-850 rounded"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="p-2 mb-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 rounded text-xs text-red-700 dark:text-red-300">{error}</div>
      )}

      {/* List */}
      <div className="flex-1 min-h-0 overflow-y-auto border border-gray-200 dark:border-surface-800 rounded bg-white dark:bg-surface-900">
        {loading ? (
          <div className="p-4 text-center text-sm text-gray-500">
            <Loader2 size={14} className="animate-spin inline mr-1" />Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="p-4 text-center text-sm text-gray-500">No backlog items match the current filters.</div>
        ) : (
          items.map((item) => {
            const PrioIcon = PRIORITY_META[item.priority].icon;
            return (
              <div key={item.id} className="px-3 py-2 border-b border-gray-100 dark:border-surface-800">
                <div className="flex items-start gap-2">
                  <PrioIcon size={14} className={`${PRIORITY_META[item.priority].color} flex-shrink-0 mt-0.5`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-gray-900 dark:text-white font-medium truncate">{item.title}</div>
                    {item.description && (
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">{item.description}</div>
                    )}
                    <div className="flex items-center gap-1 mt-1.5 flex-wrap">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${STATUS_BG[item.status]}`}>
                        {BACKLOG_STATUS_LABEL[item.status]}
                      </span>
                      {item.feature_name && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300">
                          <Rocket size={9} className="inline mr-0.5" />{item.feature_name}
                        </span>
                      )}
                      {item.source_type && item.source_type !== 'manual' && (
                        <span className="text-[10px] text-gray-500 dark:text-gray-400">
                          from {item.source_type}
                        </span>
                      )}
                      <span className="text-[10px] text-gray-400 ml-auto">{new Date(item.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <select
                      value={item.status}
                      onChange={(e) => handleStatusChange(item, e.target.value as BacklogStatus)}
                      className="text-xs px-1 py-0.5 border border-gray-300 dark:border-surface-800 rounded bg-white dark:bg-surface-850 text-gray-900 dark:text-white"
                    >
                      {Object.entries(BACKLOG_STATUS_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                    <button
                      onClick={() => handleDelete(item)}
                      title="Delete"
                      className="p-1 text-gray-400 hover:text-red-600"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
